import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  InputGroup,
  InputRightAddon,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import {
  AwardCriterionAnswerChoice,
  AwardSubCriterionAnswerFormat,
  bffApi,
  type BidV2AwardCriterionResponseDto,
  type Currency,
  type ViewDocumentAsOwnerDto,
} from "../../../../autogen/bff-api";
import type { ScorableAwardRow } from "../../../../common/award-criteria/scorableAwardRows";
import { formatNumber } from "../../../../common/formatting/formatNumber";
import { StatusAccentBar } from "../../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import type { StatusVariant } from "../../../../common/components/StatusVariant";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import {
  IncompleteFileUpload,
  isIncompleteFileUpload,
} from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { NumericInput } from "../../../../common/input/NumericInput";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { formatPercent } from "../../buyer/wizard/utils/awardCriteriaStatus";
import { useReportPendingUploads } from "../../../../common/documents/UppyUploader/pendingUploads";

// t('Answered') t('Not answered') t('Documentation required') t('Documentation provided')
// t('Documentation missing') t('Yes') t('No') t('Partly')

// A qualification of "delvis" needs emphasis and lists, not tables or images.
const partialElaborationToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

// The read DTO carries both the ids and the resolved documents; a save may have narrowed one before
// the other came back, so the truth is their union.
const attachmentIdsOf = (response: BidV2AwardCriterionResponseDto | undefined): string[] => {
  const ids = new Set<string>();
  response?.attachmentIds?.forEach((id) => ids.add(id));
  response?.attachments?.forEach((document) => ids.add(document.id));
  return [...ids];
};

const attachmentCount = (response: BidV2AwardCriterionResponseDto | undefined): number =>
  attachmentIdsOf(response).length;

/** Mirrors BidV2AwardCriterionCompleteness on the BFF — keep the two in step. */
export const isAwardCriterionAnswerComplete = (
  row: ScorableAwardRow,
  response: BidV2AwardCriterionResponseDto | undefined,
  price: number | null,
): boolean => {
  if (row.isPrice) return price != null;
  const answered = (() => {
    switch (row.answerFormat) {
      case AwardSubCriterionAnswerFormat.FreeText:
        return (response?.text ?? "").trim().length > 0;
      case AwardSubCriterionAnswerFormat.Number:
        return response?.number != null;
      case AwardSubCriterionAnswerFormat.YesNoPartial:
        // "Partly" only says something once the supplier says what it covers; Yes and No stand alone.
        if (response?.choice === AwardCriterionAnswerChoice.Partial) {
          return !isTiptapContentEmpty(response.partialElaboration ?? "");
        }
        return response?.choice != null;
      case AwardSubCriterionAnswerFormat.Document:
        return attachmentCount(response) > 0;
    }
  })();
  return answered && (!row.requiresDocumentation || attachmentCount(response) > 0);
};

const acceptsAttachments = (row: ScorableAwardRow): boolean =>
  !row.isPrice && (row.requiresDocumentation || row.answerFormat === AwardSubCriterionAnswerFormat.Document);

interface Props {
  bidId: string;
  row: ScorableAwardRow;
  response: BidV2AwardCriterionResponseDto | undefined;
  price: number | null;
  priceComesFromProducts?: boolean;
  priceEditingLocked?: boolean;
  currency: Currency;
  isReadOnly: boolean;
  onChange: (patch: Partial<BidV2AwardCriterionResponseDto>) => void;
  onPriceChange: (value: number | null) => void;
}

export const AwardCriterionAnswerCard = ({
  bidId,
  row,
  response,
  price,
  priceComesFromProducts = false,
  priceEditingLocked = false,
  currency,
  isReadOnly,
  onChange,
  onPriceChange,
}: Props) => {
  const { t } = useTranslation();
  const complete = isAwardCriterionAnswerComplete(row, response, price);
  const variant: StatusVariant = complete ? "ok" : "neutral";
  const title = row.name?.trim() || row.groupLabel;

  return (
    <Box position="relative" overflow="hidden" borderWidth="1px" borderColor="smBorder" rounded="lg" bg="smBackground">
      <StatusAccentBar variant={variant} />
      <Box px="4" py="3">
        <Stack spacing="3">
          <Flex justify="space-between" align="start" gap="3" flexWrap="wrap">
            <Stack spacing="1" flex="1" minW="0">
              <Heading size="sm">{title}</Heading>
              {!isTiptapContentEmpty(row.description) && (
                <Box fontSize="sm" color="smMuted">
                  <TipTap content={row.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
                </Box>
              )}
              {row.requiresDocumentation && (
                <Box>
                  <Badge bg="smWarningSoft" color="smWarning" textTransform="none" borderRadius="md" px="2" py="0.5">
                    {t("Documentation required")}
                  </Badge>
                </Box>
              )}
            </Stack>
            <Stack spacing="1" align="end" flexShrink={0}>
              {row.effectiveWeightPercent != null && (
                <Text fontSize="xs" color="smMuted">
                  {row.ownWeightPercent != null && row.parentId != null
                    ? t("Weight {{own}} → {{effective}} of the total", {
                        own: formatPercent(row.ownWeightPercent),
                        effective: formatPercent(row.effectiveWeightPercent),
                      })
                    : t("Weight {{effective}} of the total", {
                        effective: formatPercent(row.effectiveWeightPercent),
                      })}
                </Text>
              )}
              <StatusDotLabel variant={variant} label={complete ? t("Answered") : t("Not answered")} />
            </Stack>
          </Flex>

          <AwardCriterionAnswerInput
            row={row}
            response={response}
            price={price}
            priceComesFromProducts={priceComesFromProducts}
            priceEditingLocked={priceEditingLocked}
            currency={currency}
            isReadOnly={isReadOnly}
            onChange={onChange}
            onPriceChange={onPriceChange}
          />

          {acceptsAttachments(row) && (
            <AwardCriterionAttachments
              bidId={bidId}
              row={row}
              documents={response?.attachments ?? []}
              attachmentIds={attachmentIdsOf(response)}
              isReadOnly={isReadOnly}
              onChange={onChange}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

const AwardCriterionAnswerInput = ({
  row,
  response,
  price,
  priceComesFromProducts,
  priceEditingLocked,
  currency,
  isReadOnly,
  onChange,
  onPriceChange,
}: Omit<Props, "bidId">) => {
  const { t } = useTranslation();

  // The price rows share one figure: the tendered total, which the price model scores for all of them.
  if (row.isPrice) {
    if (priceComesFromProducts) {
      return (
        <Box borderWidth="1px" borderRadius="md" p="4">
          <Text fontSize="sm" fontWeight="medium" mb="2">
            {t("Total price")}
          </Text>
          <Text fontSize="lg" fontWeight="semibold">
            {price != null ? `${formatNumber(price)} ${currency}` : t("No products priced yet")}
          </Text>
          <Text fontSize="xs" color="smMuted" mt="2">
            {t("Calculated from your product prices. The buyer scores price automatically against the lowest delivered price.")}
          </Text>
        </Box>
      );
    }
    return (
      <Box borderWidth="1px" borderRadius="md" p="4">
        <Text fontSize="sm" fontWeight="medium" mb="2">
          {t("Your answer (number)")}
        </Text>
        <InputGroup maxW="sm">
          <NumericInput
            numericValue={price}
            onNumericChange={onPriceChange}
            isDisabled={isReadOnly || priceEditingLocked}
            placeholder={t("Enter your total price") ?? ""}
          />
          <InputRightAddon>{currency}</InputRightAddon>
        </InputGroup>
        <Text fontSize="xs" color="smMuted" mt="2">
          {t("The buyer scores price automatically against the lowest delivered price.")}
        </Text>
      </Box>
    );
  }

  return (
    <Box borderWidth="1px" borderRadius="md" p="4">
      {row.answerFormat === AwardSubCriterionAnswerFormat.FreeText && (
        <>
          <Text fontSize="sm" fontWeight="medium" mb="2">
            {t("Your answer (free text)")}
          </Text>
          <Textarea
            rows={4}
            placeholder={String(t("Write your answer here..."))}
            isReadOnly={isReadOnly}
            value={response?.text ?? ""}
            onChange={(e) => onChange({ text: e.target.value })}
          />
        </>
      )}

      {row.answerFormat === AwardSubCriterionAnswerFormat.Number && (
        <>
          <Text fontSize="sm" fontWeight="medium" mb="2">
            {t("Your answer (number)")}
          </Text>
          <Box maxW="sm">
            <NumericInput
              numericValue={response?.number ?? null}
              onNumericChange={(next) => onChange({ number: next })}
              isDisabled={isReadOnly}
              placeholder={t("Enter a number") ?? ""}
            />
          </Box>
        </>
      )}

      {row.answerFormat === AwardSubCriterionAnswerFormat.YesNoPartial && (
        <>
          <Text fontSize="sm" fontWeight="medium" mb="2">
            {t("Your answer")}
          </Text>
          <HStack spacing="2">
            {[
              { value: AwardCriterionAnswerChoice.Yes, label: t("Yes") },
              { value: AwardCriterionAnswerChoice.No, label: t("No") },
              { value: AwardCriterionAnswerChoice.Partial, label: t("Partly") },
            ].map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={response?.choice === option.value ? "solid" : "outline"}
                colorScheme="teal"
                isDisabled={isReadOnly}
                onClick={() => onChange({ choice: option.value })}
              >
                {option.label}
              </Button>
            ))}
          </HStack>

          {/* A bare "delvis" tells the evaluator nothing, so choosing it opens the room to qualify it. */}
          {response?.choice === AwardCriterionAnswerChoice.Partial && (
            <Box mt="3">
              <Text fontSize="sm" fontWeight="medium" mb="2">
                {t("What does your offer cover?")}
              </Text>
              {/* debounceWait=0 leaves the pacing to the save coordinator, as the cover letter does. */}
              <TipTap
                content={response.partialElaboration ?? ""}
                editable={!isReadOnly}
                onUpdate={(value) => onChange({ partialElaboration: value })}
                toolbar={partialElaborationToolbar}
                debounceWait={0}
                maxHeight="48"
              />
            </Box>
          )}
        </>
      )}

      {row.answerFormat === AwardSubCriterionAnswerFormat.Document && (
        <Text fontSize="sm" color="smMuted">
          {t("Answer this criterion by uploading the document the buyer asked for.")}
        </Text>
      )}
    </Box>
  );
};

const AwardCriterionAttachments = ({
  bidId,
  row,
  documents: responseDocuments,
  attachmentIds,
  isReadOnly,
  onChange,
}: {
  bidId: string;
  row: ScorableAwardRow;
  documents: ViewDocumentAsOwnerDto[];
  attachmentIds: string[];
  isReadOnly: boolean;
  onChange: (patch: Partial<BidV2AwardCriterionResponseDto>) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const count = attachmentIds.length;
  const variant: StatusVariant = count > 0 ? "ok" : row.requiresDocumentation ? "issue" : "neutral";
  const label =
    count > 0 ? t("Documentation provided") : row.requiresDocumentation ? t("Documentation missing") : t("Attachments");

  // Stable across renders: useUppyUploader rebuilds its Uppy instance whenever this identity changes,
  // and destroying the old one cancels whatever it was uploading. Every other call site passes a
  // stable reference for the same reason.
  const reloadEntity = useCallback(() => {
    void dispatch(bffApi.endpoints.getBidV2.initiate({ bidId }, { forceRefetch: true })).unwrap();
  }, [dispatch, bidId]);

  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityType: "BidV2AwardCriterionAttachment",
    entityId: bidId,
    awardCriterionId: row.id,
    reloadEntity,
  });
  useReportPendingUploads(allFiles.some(isIncompleteFileUpload));

  useEffect(() => {
    setDocuments(responseDocuments);
  }, [responseDocuments, setDocuments]);

  // Attachments can only be narrowed through the patch (the BFF rejects an id the bid does not own),
  // so removal is a filter over what is already attached.
  const removeDocument = async (document: ViewDocumentAsOwnerDto) => {
    onChange({ attachmentIds: attachmentIds.filter((id) => id !== document.id) });
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="md" p="3">
      <Flex mb="2" justify="space-between" align="center" gap="2">
        <HStack spacing="2" align="center">
          <Icon as={FiFileText} color="smMuted" aria-hidden />
          <Text fontSize="sm" fontWeight="semibold">
            {t("Documentation")}
          </Text>
        </HStack>
        <StatusDotLabel variant={variant} label={label} />
      </Flex>
      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        removeUploadedDocument={removeDocument}
        isTagsColumnVisible={false}
        isCategoryColumnVisible={false}
        canDeleteDocuments={!isReadOnly}
        canEditDocumentNames={!isReadOnly}
        documentUploadConfig={
          !isReadOnly
            ? {
                uppy,
                dropTargetId: `award-criterion-attachment-${bidId}-${row.id}`,
                dropTargetHelperText: t("Drag and drop files here, or click to browse"),
                uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
                removeUpload,
              }
            : undefined
        }
        refetchDocuments={() => {
          reloadEntity();
        }}
      />
    </Box>
  );
};
